import { all, call, fork, put, select, take } from "redux-saga/effects";

// Lib
import { hexEncode } from "lib/helpers";

// Store
import { RootState } from "../store";

// Actions
import * as actionsCreator from "../reducers/settings";
import { hideLoader, showLoader } from "reducers/loader";
import { handlerUpdateContent } from "pages/LibrariesCompounds/reducer";
import { clearListProjects, handlerChangeCurrentProject } from "pages/Projects/reducer";

export const settingsState = (state: any) => state.settings;

// Type
export type ApiResponse<Data extends unknown> = {
  data?: Data;
  ok?: boolean;
  status?: number;
};
type ActionTypeListImg = { type: string, payload: string[] };

function* _select<T>(fn: (state: RootState) => T) {
  const res: T = yield select(fn);
  return res;
}

export const librariesCompounds = (store: RootState) => store.librariesCompounds;

export function* settingsSagaStart(api: any, action: any) {
  yield fork(logoutWatcher, api, action);
  yield fork(getListImgWatcher, api, action);
  yield fork(changePriceAppWacther, api, action);
  yield fork(apiRequestResponseWatcher, api, action);

  yield fork(getAddressesCountriesWatcher, api, action);
  yield fork(getUsaStatesWatcher, api, action);

  yield fork(getPaymentsTokenWatcher, api, action);
}

/**
 * Get payments token Watcher Sagas
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getPaymentsTokenWatcher(api: any, action: any): Generator<any, void, any> {
  while (true) {
    try {
      const actions: any = yield take(actionsCreator.getPaymentsToken.type);

      const id = actions.payload;

      const res = yield call(api.request, 'paymentsHostedPage', { merchantRefId: id });

      if(!res.ok) {
        yield put(actionsCreator.getPaymentsTokenError());
      } else {
        yield put(actionsCreator.getPaymentsTokenSuccess(res.data));
      }
    } catch (e) {
      console.log("CATCH ERROR: getPaymentsTokenWatcher", e)
    }
  }
}

/**
 * Get Usa States Watcher Sagas
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getUsaStatesWatcher(api: any, action: any) {
  while(true) {
    try {
      yield take(actionsCreator.getUsaStates.type);

      const res: ApiResponse<actionsCreator.usaStateItem[]> = yield call(api.request, 'usaStates');

      if(!res.ok) {
        yield put(actionsCreator.getUsaStatesError());
      } else {
        yield put(actionsCreator.getUsaStatesSuccess(res.data));
      }
    } catch (e) {
      console.log("CATCH ERROR: getAddressesCountriesWatcher", e)
    }
  }
}

/**
 * Get addresses countries Watcher Sagas
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getAddressesCountriesWatcher(api: any, action: any) {
  while (true) {
    try {
      yield take(actionsCreator.getAddressesCountries.type);

      const res: ApiResponse<actionsCreator.countryItem[]> = yield call(api.request, 'addressesCountries');

      if (!res?.ok) {
        yield put(actionsCreator.getAddressesCountriesError());
      } else {
        yield put(actionsCreator.getAddressesCountriesSuccess(res?.data?.concat() || []));
      }
    } catch (e) {
      console.log("CATCH ERROR: getAddressesCountriesWatcher", e);
    }
  }
}

/**
 * logout Watcher Sagas
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* logoutWatcher(api: any, action: any) {
  while (true) {
    try {
      yield take(actionsCreator.logout.type);

      const auth = JSON.parse(localStorage.getItem("auth") || "{}");

      const res: ApiResponse<any> = yield call(api.request, "logout", auth.token);

      if (!res?.ok) {
        yield put(actionsCreator.logoutError());
      } else {
        yield put(actionsCreator.logoutSuccess());
        yield put(handlerChangeCurrentProject(""));
        yield put(clearListProjects());
      }
    } catch (e) {
      console.log("CATCH ERROR: logoutWatcher", e);
    }
  }
}

/**
 * Get list img saga watcher
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* getListImgWatcher(api: any, action: any): Generator<any, void, any> {
  while (true) {
    try {
      const actions: ActionTypeListImg = yield take(actionsCreator.getListImg.type);
      const data = actions.payload;

      if (data.length) {
        const response = yield all(
          data?.map((item: string) => call(
            api.request,
            "chemutilsSmilesImage",
            hexEncode(item, true))
          )
        );

        if (response.length !== response.filter((item: any) => item.ok).length) {
          yield put(actionsCreator.getListImgError());
        } else {
          const convertData = response.map((item: any, i: number) => {
            return {
              oldItem: data[i],
              newItem: URL.createObjectURL(item.data)
            };
          });
          yield put(actionsCreator.getListImgSuccess(convertData || []));
        }
      } else {
        yield put(actionsCreator.getListImgError());
      }
    } catch (e) {
      console.log("CATCH ERROR in getListImgWatcher", e);
    }
  }
}

/**
 * Change price app saga watcher
 *
 * @param {function} api - api object
 * @param {object} action - action from dispatch
 * @returns
 */
export function* changePriceAppWacther(api: any, action: any): Generator<any, void, any> {
  while (true) {
    try {
      const actions = yield take(actionsCreator.handlerChangePriceApp.type);

      const state = yield* _select(librariesCompounds);
      const content = state?.data?.content || [];

      const data = content?.map((item: any) => ({
        ...item,
        currentPrice: actions.payload
      }));

      yield put(handlerUpdateContent(data));
    } catch (e) {
      console.log("CATCH ERROR in changePriceAppWacther", e);
    }
  }
}

/**
 * API request/response watcher. Log requests and manage "small" preloader
 *
 * @returns
 */
export function* apiRequestResponseWatcher(api: any, action: any): Generator<any, void, any> {
  while (true) {
    const actions = yield take([
      actionsCreator.apiRequest.type,
      actionsCreator.apiResponse.type
    ]);

    const settings = yield select(settingsState);

    switch (actions.type) {
      case "API_REQUEST":
        if (settings.activeRequests.length && !settings.showSmall) {
          yield put(showLoader());
        }
        break;
      case "API_RESPONSE":
        if (!settings.settings.length && settings.showSmall) {
          yield put(hideLoader());
        }
        break;
      default:
    }
  }
}
